/* The persistent, filterable record table — "Out of the Menus" Part 2
   (designs/navigation-redesign.md NR8), dev surface only. A directory
   section opens straight onto its records: a table (name · state · folder ·
   charge · span · Open), state filter chips with counts, the user's folders
   as a FILTER RAIL beside the list (reusing the picker's dirPickerRows —
   a selector, not a gate), a group lens, a search box ⌘K focuses, and
   + New draft. Everything renders from already-loaded state; the pure
   slicing lives in src/shared/record-table.ts. Non-negotiables kept: state
   is a status (grouping is a view; the record stays in its folder), "Not
   filed" stays an honest gap, and no endpoint or schema is touched. */
import { Fragment, useEffect, useRef, type CSSProperties, type JSX } from "react";
import { directoryPath, type Directory } from "../../../shared/directories";
import { RECORD_STATES, type Project } from "../../../shared/projects";
import { fmtMonth } from "../../../shared/months";
import {
  applyFilters,
  groupRecords,
  knownDirIds,
  sortRecords,
  stateCounts,
  type StateFilter,
  type TableRecord,
} from "../../../shared/record-table";
import { useAppState, type Store } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import { currentSection, dirPickerRows, type SectionDir } from "../shell/sections";
import { iPanel } from "../shell/helpText";

const depthStyle = (depth: number): CSSProperties =>
  ({ ["--depth" as string]: String(depth) }) as CSSProperties;

export function RecordsTable({
  noun,
  loadWhat,
}: {
  noun: string;
  loadWhat: string;
}): JSX.Element {
  const { state, store } = useAppState();
  const searchRef = useRef<HTMLInputElement>(null);

  /* ⌘K (Ctrl+K) jumps to the section's search — per-section scope (NR8) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (state.loadError) return <LoadError what={loadWhat} />;
  if (state.loadPending) return <Loading what={loadWhat} />;

  const label = "Projects";
  const projectById = new Map(state.projects.map((p) => [p.id, p]));
  const records: TableRecord[] = state.projects.map((p) => ({
    id: p.id,
    name: p.name,
    state: p.recordState,
    directoryId: p.directoryId,
  }));
  const sectionDirs = state.directories.filter((d) => d.section === "projects");
  const known = knownDirIds(sectionDirs);

  /* chips count the current folder + search slice, before the state chip cuts */
  const slice = applyFilters(
    records,
    { state: "all", dir: state.tableDir, query: state.tableQuery },
    sectionDirs,
  );
  const counts = stateCounts(slice);
  const visible = applyFilters(
    records,
    { state: state.tableState, dir: state.tableDir, query: state.tableQuery },
    sectionDirs,
  );
  const groups = groupRecords(visible, state.tableGroup, sectionDirs, known).map((g) => ({
    ...g,
    rows: sortRecords(g.rows, state.tableSort, state.tableSortAsc),
  }));

  const open = (id: string): void => store.openProject(id);
  const newDraft = (): void => store.openForm(null);
  const sortBy = (key: "name" | "state"): void =>
    store.update(
      state.tableSort === key
        ? { tableSortAsc: !state.tableSortAsc }
        : { tableSort: key, tableSortAsc: true },
    );
  const chip = (s: StateFilter, text: string): JSX.Element => (
    <button
      key={s}
      className={"fchip" + (state.tableState === s ? " on" : "")}
      aria-pressed={state.tableState === s}
      onClick={() => store.update({ tableState: s })}
    >
      {text} <span className="fchip-n">{counts[s]}</span>
    </button>
  );

  const n = records.length;
  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>{label}.</b>
          <span>
            {n} {noun}
            {n === 1 ? "" : "s"} — the table is the list; filters and grouping are views, never a
            re-filing.
          </span>
        </div>
      }
      footnote={
        <span>
          Draft, review, active, and archived are a {noun}'s <b>status</b>, shown on it — not
          places it lives. "Not filed" is an honest gap: nothing is defaulted into a folder.
        </span>
      }
    >
      {state.listError ? (
        <p className="form-error" role="alert" tabIndex={-1}>
          {state.listError}
        </p>
      ) : null}
      <div className="dash">
        <div className="dash-top">
          <label className="dash-search">
            <span className="sr-only">Search {label}</span>
            <input
              ref={searchRef}
              type="search"
              autoComplete="off"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={state.tableQuery}
              onChange={(e) => store.update({ tableQuery: e.target.value })}
            />
            <kbd className="dash-kbd" aria-hidden="true">
              ⌘K
            </kbd>
          </label>
          <button className="btn primary dash-new" onClick={newDraft}>
            + New draft
          </button>
        </div>
        <div className="dash-filters">
          {chip("all", "All")}
          {RECORD_STATES.map((s) => chip(s, s[0]!.toUpperCase() + s.slice(1)))}
          <button
            className="grp-toggle"
            onClick={() =>
              store.update({ tableGroup: state.tableGroup === "folder" ? "state" : "folder" })
            }
          >
            Group: {state.tableGroup === "folder" ? "Folder" : "State"} ▾
          </button>
        </div>
        <div className="dash-body">
          <FolderRail store={store} dirs={sectionDirs} noun={noun} />
          <div className="dash-table">
            <table className="recs">
              <thead>
                <tr>
                  <th aria-sort={state.tableSort === "name" ? (state.tableSortAsc ? "ascending" : "descending") : undefined}>
                    <button className="th-sort" onClick={() => sortBy("name")}>
                      {"Project"}
                    </button>
                  </th>
                  <th aria-sort={state.tableSort === "state" ? (state.tableSortAsc ? "ascending" : "descending") : undefined}>
                    <button className="th-sort" onClick={() => sortBy("state")}>
                      State
                    </button>
                  </th>
                  <th>Folder</th>
                  <th>Charge</th>
                  <th>Span</th>
                  <th>
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td className="recs-empty" colSpan={6}>
                      {n === 0
                        ? `No ${noun}s recorded yet — + New draft starts one.`
                        : `Nothing matches — clear the search or filters.`}
                    </td>
                  </tr>
                ) : (
                  groups.map((g) => (
                    <Fragment key={g.key || "unfiled"}>
                      <tr className="group-row">
                        <td colSpan={6}>
                          {g.label} <span className="group-n">{g.rows.length}</span>
                        </td>
                      </tr>
                      {g.rows.map((r) => (
                        <Row
                          key={r.id}
                          r={r}
                          project={projectById.get(r.id)}
                          dirs={state.directories}
                          known={known}
                          open={open}
                        />
                      ))}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ViewFrame>
  );
}

/* one record row — the name and Open both open it (1 click, NR8) */
function Row({
  r,
  project,
  dirs,
  known,
  open,
}: {
  r: TableRecord;
  project: Project | undefined;
  dirs: Directory[];
  known: Set<string>;
  open: (id: string) => void;
}): JSX.Element {
  const filed = r.directoryId && known.has(r.directoryId) ? r.directoryId : null;
  const folder = filed ? dirs.find((d) => d.id === filed) : undefined;
  return (
    <tr className="rec">
      <td>
        <button className="rec-name" onClick={() => open(r.id)}>
          {r.name}
        </button>
      </td>
      <td>
        <span className={`state record-status status-${r.state}`}>{r.state}</span>
      </td>
      <td className="rec-folder" title={filed ? directoryPath(filed, dirs) : undefined}>
        {folder ? folder.name : <span className="rec-unfiled">Not filed</span>}
      </td>
      <td className="rec-mono">{chargeCell(project)}</td>
      <td className="rec-span">{spanCell(project)}</td>
      <td>
        <button className="rec-open" onClick={() => open(r.id)}>
          Open
        </button>
      </td>
    </tr>
  );
}

/* honest cells (settled decision 2): a gap reads as the red chip, an
   unconfirmed value wears the orange badge — never a dash-as-data */
const gap = <span className="state missing">not recorded</span>;

function chargeCell(p: Project | undefined): JSX.Element {
  if (!p?.chargeCode) return gap;
  return (
    <>
      {p.chargeCode.code}
      {p.chargeCode.unconfirmed && <span className="state unconfirmed">unconfirmed</span>}
    </>
  );
}

function spanCell(p: Project | undefined): JSX.Element {
  if (!p?.startMonth && !p?.endMonth) return gap;
  const side = (m: { month: string; unconfirmed: boolean } | null): JSX.Element =>
    m ? (
      <>
        {fmtMonth(m.month)}
        {m.unconfirmed && <span className="state unconfirmed">unconfirmed</span>}
      </>
    ) : (
      gap
    );
  return (
    <>
      {side(p.startMonth)} – {side(p.endMonth)}
    </>
  );
}

/* ---- the folder rail: the picker's tree, demoted to one filter among
   several (NR8) — reuses dirPickerRows so counts, nesting, and fold state
   read exactly as the picker's did; New folder… moves here with it ---- */

function FolderRail({
  store,
  dirs,
  noun,
}: {
  store: Store;
  dirs: Directory[];
  noun: string;
}): JSX.Element {
  const { state } = useAppState();
  const sd = currentSection(store)?.dir;
  const rows = sd && !state.dirsError && !state.dirsPending ? dirPickerRows(state, sd) : [];
  const railRows = rows.filter((row) => row.kind !== "record");
  return (
    <div className="dash-rail">
      <p className="rail-h">Folders</p>
      <button
        className={"rail-row rail-all" + (state.tableDir === "all" ? " on" : "")}
        onClick={() => store.selectTableDir("all")}
      >
        <span className="folder-icon all" aria-hidden="true" />
        <span className="rail-name">All {noun}s</span>
      </button>
      {state.dirsError ? (
        <p className="rail-note dirs-error">
          Folders not recorded — couldn't load. Records still list and search above.
        </p>
      ) : state.dirsPending ? (
        <p className="rail-note" aria-busy="true">
          Loading folders…
        </p>
      ) : (
        railRows.map((row) => {
          const id = row.kind === "dir" ? row.dir.id : "";
          const name = row.kind === "dir" ? row.dir.name : "Not filed";
          const on = state.tableDir === id;
          return (
            <button
              key={id || "unfiled"}
              className={
                "rail-row" +
                (on ? " on" : "") +
                (row.kind === "unfiled" ? " unfiled" : "")
              }
              style={depthStyle(row.depth)}
              aria-expanded={row.kind === "dir" ? row.open : undefined}
              aria-pressed={on}
              onClick={() => store.selectTableDir(id)}
            >
              <span className="twisty" aria-hidden="true">
                {row.kind === "dir" ? (row.open ? "▾" : "▸") : ""}
              </span>
              <span className="folder-icon" aria-hidden="true" />
              <span className="rail-name">{name}</span>
              <span className="rail-count">{row.count}</span>
            </button>
          );
        })
      )}
      {sd && !state.dirsError && (
        <RailNewFolder store={store} sd={sd} dirs={dirs} />
      )}
    </div>
  );
}

/* the picker's New-folder form, relocated beside the rail (NR8): same store
   actions, same DIR4/DIR6 semantics — the selected rail folder is the parent */
function RailNewFolder({
  store,
  sd,
  dirs,
}: {
  store: Store;
  sd: SectionDir;
  dirs: Directory[];
}): JSX.Element {
  const { state } = useAppState();
  const nameRef = useRef<HTMLInputElement>(null);
  if (!state.newFolderOpen)
    return (
      <button className="rail-new btn ghost" onClick={() => store.openNewFolder()}>
        New folder…
      </button>
    );
  const into = state.selectedDirId ? directoryPath(state.selectedDirId, dirs) : "the top level";
  const create = (): void => void store.createFolder(sd.section, nameRef.current?.value ?? "");
  return (
    <div className="new-folder rail-new-form">
      {store.on("about-hermes") && (
        <div
          style={{ display: "contents" }}
          dangerouslySetInnerHTML={{ __html: iPanel(state.me.surface, "directories") }}
        />
      )}
      <label className="field">
        <span className="field-label">New folder in {into}</span>
        <input
          ref={nameRef}
          className="new-folder-name"
          type="text"
          placeholder="e.g. Mining"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") create();
          }}
        />
      </label>
      <p className="form-error new-folder-error" role="alert">
        {state.newFolderError ?? ""}
      </p>
      <div className="form-actions">
        <button className="new-folder-create btn primary" onClick={create}>
          Create folder
        </button>
        <button className="new-folder-cancel btn ghost" onClick={() => store.cancelNewFolder()}>
          Cancel
        </button>
      </div>
    </div>
  );
}
